import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import {
  iosNativeCenterHeaderLeft,
  iosNativeContent,
  iosNativePageRoot,
  iosNativeScreenSubtitle,
  iosNativeScreenTitle,
} from '../styles/iosNativePageCss';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Leaf, Clock, Sparkles, ChevronDown, ChevronUp, Calendar } from 'lucide-react';
import type { DIYMask } from '../services/scanService';
import { useAWSAuth } from '../contexts/AWSAuthContext';
import { useSubscription } from '../hooks/useSubscription';
import { FeatureLock } from '../components/FeatureLock';
import { getDIYMaskCount } from '../types/subscription';

const PageContainer = styled.div`
  min-height: calc(100vh - 72px);
  background: linear-gradient(180deg, ${theme.colors.background.secondary} 0%, ${theme.colors.background.primary} 100%);
  padding: 2rem 1rem 4rem;

  ${iosNativePageRoot}
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;

  ${iosNativeContent}
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  animation: slideUp 0.5s ease-out;

  ${iosNativeCenterHeaderLeft}
`;

const Title = styled.h1`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize['4xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;

  ${iosNativeScreenTitle}
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  color: ${theme.colors.text.secondary};
  max-width: 600px;
  margin: 0 auto;

  body.native-app & {
    margin-left: 0;
  }

  ${iosNativeScreenSubtitle}
`;

const MasksGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  
  @media (min-width: ${theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: ${theme.breakpoints.lg}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const MaskCard = styled(Card)<{ delay?: string }>`
  animation: slideUp 0.6s ease-out;
  animation-delay: ${(props) => props.delay || '0s'};
  display: flex;
  flex-direction: column;
`;

const MaskImage = styled.div<{ $emoji: string }>`
  width: 100%;
  aspect-ratio: 1;
  background: ${theme.colors.glow.mint};
  border-radius: ${theme.borderRadius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
  margin-bottom: 1rem;

  &::before {
    content: '${props => props.$emoji}';
  }
`;

const MaskTitle = styled.h3`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

const MaskDescription = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  margin-bottom: 1rem;
  line-height: 1.6;
`;

const TagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const ExpandButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.75rem;
  background: ${theme.colors.glow.mint};
  border: none;
  border-radius: ${theme.borderRadius.lg};
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.primary[700]};
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: auto;

  &:hover {
    background: ${theme.colors.glow.sage};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const RecipeDetails = styled.div<{ $expanded: boolean }>`
  max-height: ${props => props.$expanded ? '1000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
  margin-top: ${props => props.$expanded ? '1rem' : '0'};
`;

const RecipeSection = styled.div`
  margin-bottom: 1.5rem;
`;

const RecipeSectionTitle = styled.h4`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RecipeList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const RecipeListItem = styled.li`
  padding-left: 1.5rem;
  position: relative;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  line-height: 1.6;

  &::before {
    content: '•';
    position: absolute;
    left: 0;
    color: ${theme.colors.primary[500]};
    font-weight: ${theme.typography.fontWeight.bold};
  }
`;

const ScienceNote = styled.div`
  background: ${theme.colors.accent.sky}20;
  padding: 1rem;
  border-radius: ${theme.borderRadius.lg};
  border-left: 4px solid ${theme.colors.primary[500]};
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  line-height: 1.6;
  margin-top: 1rem;
`;

interface Mask {
  id: string;
  title: string;
  emoji: string;
  description: string;
  benefits: string[];
  ingredients: string[];
  instructions: string[];
  duration: string;
  science: string;
  tags: string[];
}

const masks: Mask[] = [
  {
    id: '1',
    title: 'Activated Charcoal Detox Mask',
    emoji: '⚫',
    description: 'Deep pore-cleansing mask to draw out impurities and blackheads.',
    benefits: ['Deep cleansing', 'Removes blackheads', 'Detoxifies pores'],
    ingredients: [
      '1 tsp activated charcoal powder',
      '1 tbsp bentonite clay',
      '1 tsp apple cider vinegar',
      '2-3 tbsp water or rose water'
    ],
    instructions: [
      'Mix activated charcoal with bentonite clay',
      'Add apple cider vinegar',
      'Slowly add water until smooth paste forms',
      'Apply to clean, damp skin',
      'Let dry for 10-15 minutes',
      'Rinse with warm water, moisturize after'
    ],
    duration: '10-15 min',
    science: 'Activated charcoal has a porous structure that binds to toxins and impurities, drawing them out of pores. Bentonite clay further absorbs excess oil and debris. Apple cider vinegar balances pH and has antimicrobial properties.',
    tags: ['Deep cleanse', 'Detoxifying', 'Oily skin'],
  },
  {
    id: '2',
    title: 'Rosehip + Vitamin E Repair Mask',
    emoji: '🌹',
    description: 'Regenerative mask to repair damaged skin and fade scars.',
    benefits: ['Repairs skin', 'Fades scars', 'Anti-aging'],
    ingredients: [
      '1 tbsp rosehip seed oil',
      '1 tsp vitamin E oil',
      '1 tbsp aloe vera gel',
      '1 tsp honey'
    ],
    instructions: [
      'Mix rosehip oil with vitamin E oil',
      'Add aloe vera gel and honey',
      'Warm slightly for better absorption',
      'Apply to clean face and neck',
      'Massage gently for 2 minutes',
      'Leave on for 20-30 minutes',
      'Rinse with lukewarm water'
    ],
    duration: '20-30 min',
    science: 'Rosehip oil is rich in retinoic acid (vitamin A), omega-3 and omega-6 fatty acids that promote cell regeneration and fade hyperpigmentation. Vitamin E is a powerful antioxidant that protects and repairs skin cells.',
    tags: ['Repair', 'Scar fading', 'Anti-aging'],
  },
  {
    id: '3',
    title: 'Sulfur + Clay Acne Treatment',
    emoji: '🌋',
    description: 'Powerful spot treatment mask for active breakouts and cystic acne.',
    benefits: ['Fights acne', 'Reduces inflammation', 'Dries breakouts'],
    ingredients: [
      '1 tsp sulfur powder',
      '2 tbsp kaolin clay',
      '1 tsp tea tree oil',
      '2-3 tbsp water'
    ],
    instructions: [
      'Mix sulfur powder with kaolin clay',
      'Add tea tree oil',
      'Slowly add water to form paste',
      'Apply as spot treatment to active breakouts',
      'Leave on for 10-15 minutes',
      'Rinse thoroughly with cool water',
      'Use 2-3 times per week maximum'
    ],
    duration: '10-15 min',
    science: 'Sulfur has antimicrobial and keratolytic properties that kill acne-causing bacteria and help shed dead skin cells. Kaolin clay absorbs excess oil without over-drying. Tea tree oil provides additional antibacterial benefits.',
    tags: ['Acne-fighting', 'Spot treatment', 'Oily skin'],
  },
  {
    id: '4',
    title: 'Colloidal Oatmeal + Milk Soothing Mask',
    emoji: '🥛',
    description: 'Ultra-gentle mask for sensitive, eczema-prone, or irritated skin.',
    benefits: ['Soothes irritation', 'Calms eczema', 'Repairs barrier'],
    ingredients: [
      '2 tbsp colloidal oatmeal (or finely ground oats)',
      '2 tbsp whole milk or oat milk',
      '1 tsp honey',
      '1 tsp aloe vera gel'
    ],
    instructions: [
      'Mix colloidal oatmeal with milk',
      'Add honey and aloe vera',
      'Stir until smooth paste forms',
      'Apply to clean, damp skin',
      'Leave on for 15-20 minutes',
      'Gently rinse with cool water',
      'Pat dry, no rubbing'
    ],
    duration: '15-20 min',
    science: 'Colloidal oatmeal contains beta-glucans and avenanthramides that reduce inflammation and strengthen the skin barrier. Milk\'s lactic acid gently exfoliates while its fat content soothes and moisturizes. This combination is FDA-approved for eczema relief.',
    tags: ['Sensitive skin', 'Eczema', 'Barrier repair'],
  },
  {
    id: '5',
    title: 'Pineapple + Papaya Enzyme Brightening',
    emoji: '🍍',
    description: 'Dual enzyme mask to brighten dull skin and fade dark spots.',
    benefits: ['Brightens skin', 'Fades dark spots', 'Smooths texture'],
    ingredients: [
      '1/4 cup fresh pineapple, blended',
      '1/4 cup ripe papaya, mashed',
      '1 tsp honey',
      '1 tsp plain yogurt'
    ],
    instructions: [
      'Blend pineapple until smooth',
      'Mash papaya separately',
      'Combine pineapple, papaya, honey, and yogurt',
      'Mix until smooth',
      'Apply to clean face',
      'Leave on for 15-20 minutes',
      'Gently massage while rinsing with warm water'
    ],
    duration: '15-20 min',
    science: 'Pineapple contains bromelain, an enzyme that breaks down dead skin cells and reduces inflammation. Papaya contains papain, another proteolytic enzyme that exfoliates and brightens. Together, they provide powerful but gentle exfoliation.',
    tags: ['Brightening', 'Enzyme', 'Exfoliating'],
  },
  {
    id: '6',
    title: 'Seaweed + Spirulina Detox Mask',
    emoji: '🌊',
    description: 'Mineral-rich mask to detoxify and nourish skin with marine nutrients.',
    benefits: ['Detoxifies', 'Mineral boost', 'Anti-inflammatory'],
    ingredients: [
      '1 tbsp spirulina powder',
      '1 tbsp kelp powder (or crushed seaweed)',
      '1 tbsp honey',
      '2-3 tbsp water'
    ],
    instructions: [
      'Mix spirulina and kelp powder',
      'Add honey',
      'Slowly add water to form smooth paste',
      'Apply to clean face and neck',
      'Leave on for 15-20 minutes',
      'Rinse with cool water',
      'Moisturize after'
    ],
    duration: '15-20 min',
    science: 'Spirulina is rich in phycocyanin, an antioxidant that reduces inflammation and protects against free radicals. Seaweed contains minerals like iodine, zinc, and magnesium that support skin health. Both are rich in amino acids that strengthen the skin barrier.',
    tags: ['Detoxifying', 'Mineral-rich', 'Anti-inflammatory'],
  },
  {
    id: '7',
    title: 'Lavender + Chamomile Sleep Mask',
    emoji: '🌙',
    description: 'Calming overnight mask to repair skin while you sleep.',
    benefits: ['Overnight repair', 'Calms stress', 'Reduces redness'],
    ingredients: [
      '1 tbsp chamomile tea (brewed and cooled)',
      '1 tbsp aloe vera gel',
      '2-3 drops lavender essential oil',
      '1 tsp jojoba oil'
    ],
    instructions: [
      'Brew chamomile tea and let cool completely',
      'Mix with aloe vera gel',
      'Add lavender oil and jojoba oil',
      'Apply to clean face before bed',
      'Leave on overnight',
      'Rinse with cool water in the morning'
    ],
    duration: 'Overnight',
    science: 'Chamomile contains bisabolol and chamazulene that reduce inflammation and calm irritated skin. Lavender oil has antimicrobial properties and promotes relaxation, which reduces stress-related breakouts. Jojoba oil closely mimics skin\'s natural sebum.',
    tags: ['Overnight', 'Calming', 'Repair'],
  },
  {
    id: '8',
    title: 'Apple Cider Vinegar + Clay Balancing Mask',
    emoji: '🍎',
    description: 'pH-balancing mask to restore skin\'s natural acidity and control oil.',
    benefits: ['Balances pH', 'Controls oil', 'Tightens pores'],
    ingredients: [
      '2 tbsp bentonite or kaolin clay',
      '1 tbsp raw apple cider vinegar',
      '1 tsp honey',
      '1-2 tbsp water'
    ],
    instructions: [
      'Mix clay with apple cider vinegar',
      'Add honey',
      'Slowly add water until smooth paste',
      'Apply to clean, damp skin',
      'Let dry for 10-12 minutes',
      'Rinse with cool water',
      'Tone and moisturize after'
    ],
    duration: '10-12 min',
    science: 'Apple cider vinegar contains acetic acid that helps restore skin\'s natural pH (around 5.5), which is crucial for barrier function. Clay absorbs excess oil and impurities. The combination helps balance sebum production and tighten pores.',
    tags: ['pH balancing', 'Oil control', 'Pore tightening'],
  },
  {
    id: '9',
    title: 'Ginseng + Green Tea Anti-Aging Mask',
    emoji: '🧘',
    description: 'Powerful antioxidant mask to fight signs of aging and boost collagen.',
    benefits: ['Anti-aging', 'Boosts collagen', 'Antioxidant protection'],
    ingredients: [
      '1 tbsp ginseng powder',
      '1 tbsp matcha green tea powder',
      '1 tbsp honey',
      '1 tsp plain yogurt',
      '1/2 tsp turmeric'
    ],
    instructions: [
      'Mix ginseng and matcha powder',
      'Add honey and yogurt',
      'Stir in turmeric',
      'Apply to clean face and neck',
      'Leave on for 20-25 minutes',
      'Rinse with lukewarm water',
      'Follow with moisturizer'
    ],
    duration: '20-25 min',
    science: 'Ginseng contains ginsenosides that stimulate collagen production and improve skin elasticity. Matcha provides 137x more EGCG than regular green tea, protecting against UV damage and free radicals. Turmeric\'s curcumin further reduces inflammation and brightens.',
    tags: ['Anti-aging', 'Collagen', 'Antioxidant'],
  },
  {
    id: '10',
    title: 'Witch Hazel + Rose Water Toner Mask',
    emoji: '🌺',
    description: 'Gentle astringent mask to tighten pores and reduce inflammation.',
    benefits: ['Tightens pores', 'Reduces inflammation', 'Tones skin'],
    ingredients: [
      '2 tbsp witch hazel',
      '1 tbsp rose water',
      '1 tsp aloe vera gel',
      'Clean cotton pads or cloth'
    ],
    instructions: [
      'Mix witch hazel with rose water',
      'Add aloe vera gel',
      'Soak cotton pads in mixture',
      'Apply pads to clean face',
      'Leave on for 15-20 minutes',
      'Remove pads and pat remaining liquid into skin',
      'No need to rinse'
    ],
    duration: '15-20 min',
    science: 'Witch hazel contains tannins that act as a natural astringent, tightening pores and reducing inflammation. Rose water soothes and hydrates while providing mild astringent properties. Aloe vera adds hydration and anti-inflammatory benefits.',
    tags: ['Pore tightening', 'Toning', 'Soothing'],
  },
  {
    id: '11',
    title: 'Probiotic Yogurt + Kefir Barrier Mask',
    emoji: '🥛',
    description: 'Gut-skin connection mask to strengthen barrier with beneficial bacteria.',
    benefits: ['Strengthens barrier', 'Balances microbiome', 'Reduces inflammation'],
    ingredients: [
      '2 tbsp plain Greek yogurt',
      '1 tbsp kefir (or more yogurt)',
      '1 tsp honey',
      '1/2 tsp ground oats'
    ],
    instructions: [
      'Mix Greek yogurt with kefir',
      'Add honey and ground oats',
      'Stir until smooth',
      'Apply to clean face',
      'Leave on for 15-20 minutes',
      'Rinse with cool water',
      'Pat dry gently'
    ],
    duration: '15-20 min',
    science: 'Probiotics in yogurt and kefir support the skin\'s microbiome, which is crucial for barrier function and reducing inflammation. Lactic acid gently exfoliates while the beneficial bacteria help balance skin flora. This addresses the gut-skin connection.',
    tags: ['Barrier repair', 'Probiotic', 'Microbiome'],
  },
  {
    id: '12',
    title: 'Hyaluronic Acid + Glycerin Hydration Boost',
    emoji: '💧',
    description: 'Intensive hydration mask to plump and smooth dehydrated skin.',
    benefits: ['Deep hydration', 'Plumps skin', 'Smooths texture'],
    ingredients: [
      '1 tbsp aloe vera gel',
      '1 tsp vegetable glycerin',
      '1 tsp honey',
      '2-3 drops hyaluronic acid serum (optional)',
      '1 tbsp rose water'
    ],
    instructions: [
      'Mix aloe vera with glycerin',
      'Add honey and rose water',
      'Stir in hyaluronic acid if using',
      'Apply to clean, slightly damp face',
      'Leave on for 20-30 minutes',
      'Gently massage remaining product into skin',
      'No need to rinse completely'
    ],
    duration: '20-30 min',
    science: 'Hyaluronic acid can hold up to 1000x its weight in water, providing intense hydration. Glycerin is a humectant that draws moisture from the air into the skin. Aloe vera adds additional hydration and soothes. Together, they create a powerful hydration boost.',
    tags: ['Hydrating', 'Plumping', 'Moisturizing'],
  },
  {
    id: '13',
    title: 'Licorice Root + Niacinamide Brightening',
    emoji: '🌿',
    description: 'Advanced brightening mask to fade hyperpigmentation and even skin tone.',
    benefits: ['Fades dark spots', 'Evens tone', 'Brightens complexion'],
    ingredients: [
      '1 tbsp licorice root powder',
      '1 tsp niacinamide powder (or crushed B3 supplement)',
      '1 tbsp plain yogurt',
      '1 tsp honey',
      '1/2 tsp lemon juice'
    ],
    instructions: [
      'Mix licorice root powder with niacinamide',
      'Add yogurt and honey',
      'Stir in lemon juice',
      'Apply to clean face',
      'Leave on for 15-20 minutes',
      'Rinse with cool water',
      'Use sunscreen after (increases sun sensitivity)'
    ],
    duration: '15-20 min',
    science: 'Licorice root contains glabridin, which inhibits tyrosinase (the enzyme that produces melanin), effectively fading dark spots. Niacinamide (vitamin B3) also inhibits melanosome transfer and improves skin barrier function. Together, they provide powerful brightening effects.',
    tags: ['Brightening', 'Dark spots', 'Evening tone'],
  },
  {
    id: '14',
    title: 'Dead Sea Mud + Epsom Salt Detox',
    emoji: '🧂',
    description: 'Mineral-rich detoxifying mask to draw out toxins and reduce inflammation.',
    benefits: ['Detoxifies', 'Reduces inflammation', 'Mineral boost'],
    ingredients: [
      '2 tbsp Dead Sea mud (or bentonite clay)',
      '1 tbsp Epsom salt (dissolved)',
      '1 tsp honey',
      '2-3 tbsp water'
    ],
    instructions: [
      'Dissolve Epsom salt in warm water',
      'Mix with Dead Sea mud',
      'Add honey',
      'Apply to clean face and neck',
      'Leave on for 10-15 minutes',
      'Rinse with warm water',
      'Moisturize after'
    ],
    duration: '10-15 min',
    science: 'Dead Sea mud is rich in magnesium, calcium, and potassium that reduce inflammation and improve skin barrier function. Epsom salt (magnesium sulfate) further reduces inflammation and helps draw out toxins. The combination provides deep detoxification.',
    tags: ['Detoxifying', 'Mineral-rich', 'Anti-inflammatory'],
  },
  {
    id: '14',
    title: 'Coconut Oil + Coffee Scrub Mask',
    emoji: '☕',
    description: 'Gentle exfoliating mask to improve circulation and reduce puffiness.',
    benefits: ['Improves circulation', 'Reduces puffiness', 'Gentle exfoliation'],
    ingredients: [
      '2 tbsp used coffee grounds',
      '1 tbsp coconut oil (melted)',
      '1 tsp honey',
      '1/2 tsp brown sugar (optional)'
    ],
    instructions: [
      'Mix coffee grounds with melted coconut oil',
      'Add honey and brown sugar if using',
      'Apply in gentle circular motions',
      'Massage for 2-3 minutes',
      'Leave on for 10 minutes',
      'Rinse with warm water'
    ],
    duration: '10-12 min',
    science: 'Coffee grounds provide gentle physical exfoliation and caffeine constricts blood vessels to reduce puffiness. Coconut oil contains lauric acid with antimicrobial properties. The circular motion improves circulation.',
    tags: ['Exfoliating', 'Circulation', 'Puffiness'],
  },
  {
    id: '15',
    title: 'Activated Charcoal Detox Mask',
    emoji: '⚫',
    description: 'Deep pore-cleansing mask to draw out impurities and blackheads.',
    benefits: ['Deep cleansing', 'Removes blackheads', 'Detoxifies pores'],
    ingredients: [
      '1 tsp activated charcoal powder',
      '1 tbsp bentonite clay',
      '1 tsp apple cider vinegar',
      '2-3 tbsp water or rose water'
    ],
    instructions: [
      'Mix activated charcoal with bentonite clay',
      'Add apple cider vinegar',
      'Slowly add water until smooth paste forms',
      'Apply to clean, damp skin',
      'Let dry for 10-15 minutes',
      'Rinse with warm water, moisturize after'
    ],
    duration: '10-15 min',
    science: 'Activated charcoal has a porous structure that binds to toxins and impurities, drawing them out of pores. Bentonite clay further absorbs excess oil and debris. Apple cider vinegar balances pH and has antimicrobial properties.',
    tags: ['Deep cleanse', 'Detoxifying', 'Oily skin'],
  },
  {
    id: '16',
    title: 'Rosehip + Vitamin E Repair Mask',
    emoji: '🌹',
    description: 'Regenerative mask to repair damaged skin and fade scars.',
    benefits: ['Repairs skin', 'Fades scars', 'Anti-aging'],
    ingredients: [
      '1 tbsp rosehip seed oil',
      '1 tsp vitamin E oil',
      '1 tbsp aloe vera gel',
      '1 tsp honey'
    ],
    instructions: [
      'Mix rosehip oil with vitamin E oil',
      'Add aloe vera gel and honey',
      'Warm slightly for better absorption',
      'Apply to clean face and neck',
      'Massage gently for 2 minutes',
      'Leave on for 20-30 minutes',
      'Rinse with lukewarm water'
    ],
    duration: '20-30 min',
    science: 'Rosehip oil is rich in retinoic acid (vitamin A), omega-3 and omega-6 fatty acids that promote cell regeneration and fade hyperpigmentation. Vitamin E is a powerful antioxidant that protects and repairs skin cells.',
    tags: ['Repair', 'Scar fading', 'Anti-aging'],
  },
  {
    id: '17',
    title: 'Sulfur + Clay Acne Treatment',
    emoji: '🌋',
    description: 'Powerful spot treatment mask for active breakouts and cystic acne.',
    benefits: ['Fights acne', 'Reduces inflammation', 'Dries breakouts'],
    ingredients: [
      '1 tsp sulfur powder',
      '2 tbsp kaolin clay',
      '1 tsp tea tree oil',
      '2-3 tbsp water'
    ],
    instructions: [
      'Mix sulfur powder with kaolin clay',
      'Add tea tree oil',
      'Slowly add water to form paste',
      'Apply as spot treatment to active breakouts',
      'Leave on for 10-15 minutes',
      'Rinse thoroughly with cool water',
      'Use 2-3 times per week maximum'
    ],
    duration: '10-15 min',
    science: 'Sulfur has antimicrobial and keratolytic properties that kill acne-causing bacteria and help shed dead skin cells. Kaolin clay absorbs excess oil without over-drying. Tea tree oil provides additional antibacterial benefits.',
    tags: ['Acne-fighting', 'Spot treatment', 'Oily skin'],
  },
  {
    id: '18',
    title: 'Colloidal Oatmeal + Milk Soothing Mask',
    emoji: '🥛',
    description: 'Ultra-gentle mask for sensitive, eczema-prone, or irritated skin.',
    benefits: ['Soothes irritation', 'Calms eczema', 'Repairs barrier'],
    ingredients: [
      '2 tbsp colloidal oatmeal (or finely ground oats)',
      '2 tbsp whole milk or oat milk',
      '1 tsp honey',
      '1 tsp aloe vera gel'
    ],
    instructions: [
      'Mix colloidal oatmeal with milk',
      'Add honey and aloe vera',
      'Stir until smooth paste forms',
      'Apply to clean, damp skin',
      'Leave on for 15-20 minutes',
      'Gently rinse with cool water',
      'Pat dry, no rubbing'
    ],
    duration: '15-20 min',
    science: 'Colloidal oatmeal contains beta-glucans and avenanthramides that reduce inflammation and strengthen the skin barrier. Milk\'s lactic acid gently exfoliates while its fat content soothes and moisturizes. This combination is FDA-approved for eczema relief.',
    tags: ['Sensitive skin', 'Eczema', 'Barrier repair'],
  },
  {
    id: '19',
    title: 'Pineapple + Papaya Enzyme Brightening',
    emoji: '🍍',
    description: 'Dual enzyme mask to brighten dull skin and fade dark spots.',
    benefits: ['Brightens skin', 'Fades dark spots', 'Smooths texture'],
    ingredients: [
      '1/4 cup fresh pineapple, blended',
      '1/4 cup ripe papaya, mashed',
      '1 tsp honey',
      '1 tsp plain yogurt'
    ],
    instructions: [
      'Blend pineapple until smooth',
      'Mash papaya separately',
      'Combine pineapple, papaya, honey, and yogurt',
      'Mix until smooth',
      'Apply to clean face',
      'Leave on for 15-20 minutes',
      'Gently massage while rinsing with warm water'
    ],
    duration: '15-20 min',
    science: 'Pineapple contains bromelain, an enzyme that breaks down dead skin cells and reduces inflammation. Papaya contains papain, another proteolytic enzyme that exfoliates and brightens. Together, they provide powerful but gentle exfoliation.',
    tags: ['Brightening', 'Enzyme', 'Exfoliating'],
  },
  {
    id: '20',
    title: 'Seaweed + Spirulina Detox Mask',
    emoji: '🌊',
    description: 'Mineral-rich mask to detoxify and nourish skin with marine nutrients.',
    benefits: ['Detoxifies', 'Mineral boost', 'Anti-inflammatory'],
    ingredients: [
      '1 tbsp spirulina powder',
      '1 tbsp kelp powder (or crushed seaweed)',
      '1 tbsp honey',
      '2-3 tbsp water'
    ],
    instructions: [
      'Mix spirulina and kelp powder',
      'Add honey',
      'Slowly add water to form smooth paste',
      'Apply to clean face and neck',
      'Leave on for 15-20 minutes',
      'Rinse with cool water',
      'Moisturize after'
    ],
    duration: '15-20 min',
    science: 'Spirulina is rich in phycocyanin, an antioxidant that reduces inflammation and protects against free radicals. Seaweed contains minerals like iodine, zinc, and magnesium that support skin health. Both are rich in amino acids that strengthen the skin barrier.',
    tags: ['Detoxifying', 'Mineral-rich', 'Anti-inflammatory'],
  },
  {
    id: '21',
    title: 'Lavender + Chamomile Sleep Mask',
    emoji: '🌙',
    description: 'Calming overnight mask to repair skin while you sleep.',
    benefits: ['Overnight repair', 'Calms stress', 'Reduces redness'],
    ingredients: [
      '1 tbsp chamomile tea (brewed and cooled)',
      '1 tbsp aloe vera gel',
      '2-3 drops lavender essential oil',
      '1 tsp jojoba oil'
    ],
    instructions: [
      'Brew chamomile tea and let cool completely',
      'Mix with aloe vera gel',
      'Add lavender oil and jojoba oil',
      'Apply to clean face before bed',
      'Leave on overnight',
      'Rinse with cool water in the morning'
    ],
    duration: 'Overnight',
    science: 'Chamomile contains bisabolol and chamazulene that reduce inflammation and calm irritated skin. Lavender oil has antimicrobial properties and promotes relaxation, which reduces stress-related breakouts. Jojoba oil closely mimics skin\'s natural sebum.',
    tags: ['Overnight', 'Calming', 'Repair'],
  },
  {
    id: '22',
    title: 'Apple Cider Vinegar + Clay Balancing Mask',
    emoji: '🍎',
    description: 'pH-balancing mask to restore skin\'s natural acidity and control oil.',
    benefits: ['Balances pH', 'Controls oil', 'Tightens pores'],
    ingredients: [
      '2 tbsp bentonite or kaolin clay',
      '1 tbsp raw apple cider vinegar',
      '1 tsp honey',
      '1-2 tbsp water'
    ],
    instructions: [
      'Mix clay with apple cider vinegar',
      'Add honey',
      'Slowly add water until smooth paste',
      'Apply to clean, damp skin',
      'Let dry for 10-12 minutes',
      'Rinse with cool water',
      'Tone and moisturize after'
    ],
    duration: '10-12 min',
    science: 'Apple cider vinegar contains acetic acid that helps restore skin\'s natural pH (around 5.5), which is crucial for barrier function. Clay absorbs excess oil and impurities. The combination helps balance sebum production and tighten pores.',
    tags: ['pH balancing', 'Oil control', 'Pore tightening'],
  },
  {
    id: '23',
    title: 'Ginseng + Green Tea Anti-Aging Mask',
    emoji: '🧘',
    description: 'Powerful antioxidant mask to fight signs of aging and boost collagen.',
    benefits: ['Anti-aging', 'Boosts collagen', 'Antioxidant protection'],
    ingredients: [
      '1 tbsp ginseng powder',
      '1 tbsp matcha green tea powder',
      '1 tbsp honey',
      '1 tsp plain yogurt',
      '1/2 tsp turmeric'
    ],
    instructions: [
      'Mix ginseng and matcha powder',
      'Add honey and yogurt',
      'Stir in turmeric',
      'Apply to clean face and neck',
      'Leave on for 20-25 minutes',
      'Rinse with lukewarm water',
      'Follow with moisturizer'
    ],
    duration: '20-25 min',
    science: 'Ginseng contains ginsenosides that stimulate collagen production and improve skin elasticity. Matcha provides 137x more EGCG than regular green tea, protecting against UV damage and free radicals. Turmeric\'s curcumin further reduces inflammation and brightens.',
    tags: ['Anti-aging', 'Collagen', 'Antioxidant'],
  },
  {
    id: '24',
    title: 'Witch Hazel + Rose Water Toner Mask',
    emoji: '🌺',
    description: 'Gentle astringent mask to tighten pores and reduce inflammation.',
    benefits: ['Tightens pores', 'Reduces inflammation', 'Tones skin'],
    ingredients: [
      '2 tbsp witch hazel',
      '1 tbsp rose water',
      '1 tsp aloe vera gel',
      'Clean cotton pads or cloth'
    ],
    instructions: [
      'Mix witch hazel with rose water',
      'Add aloe vera gel',
      'Soak cotton pads in mixture',
      'Apply pads to clean face',
      'Leave on for 15-20 minutes',
      'Remove pads and pat remaining liquid into skin',
      'No need to rinse'
    ],
    duration: '15-20 min',
    science: 'Witch hazel contains tannins that act as a natural astringent, tightening pores and reducing inflammation. Rose water soothes and hydrates while providing mild astringent properties. Aloe vera adds hydration and anti-inflammatory benefits.',
    tags: ['Pore tightening', 'Toning', 'Soothing'],
  },
  {
    id: '25',
    title: 'Probiotic Yogurt + Kefir Barrier Mask',
    emoji: '🥛',
    description: 'Gut-skin connection mask to strengthen barrier with beneficial bacteria.',
    benefits: ['Strengthens barrier', 'Balances microbiome', 'Reduces inflammation'],
    ingredients: [
      '2 tbsp plain Greek yogurt',
      '1 tbsp kefir (or more yogurt)',
      '1 tsp honey',
      '1/2 tsp ground oats'
    ],
    instructions: [
      'Mix Greek yogurt with kefir',
      'Add honey and ground oats',
      'Stir until smooth',
      'Apply to clean face',
      'Leave on for 15-20 minutes',
      'Rinse with cool water',
      'Pat dry gently'
    ],
    duration: '15-20 min',
    science: 'Probiotics in yogurt and kefir support the skin\'s microbiome, which is crucial for barrier function and reducing inflammation. Lactic acid gently exfoliates while the beneficial bacteria help balance skin flora. This addresses the gut-skin connection.',
    tags: ['Barrier repair', 'Probiotic', 'Microbiome'],
  },
  {
    id: '26',
    title: 'Hyaluronic Acid + Glycerin Hydration Boost',
    emoji: '💧',
    description: 'Intensive hydration mask to plump and smooth dehydrated skin.',
    benefits: ['Deep hydration', 'Plumps skin', 'Smooths texture'],
    ingredients: [
      '1 tbsp aloe vera gel',
      '1 tsp vegetable glycerin',
      '1 tsp honey',
      '2-3 drops hyaluronic acid serum (optional)',
      '1 tbsp rose water'
    ],
    instructions: [
      'Mix aloe vera with glycerin',
      'Add honey and rose water',
      'Stir in hyaluronic acid if using',
      'Apply to clean, slightly damp face',
      'Leave on for 20-30 minutes',
      'Gently massage remaining product into skin',
      'No need to rinse completely'
    ],
    duration: '20-30 min',
    science: 'Hyaluronic acid can hold up to 1000x its weight in water, providing intense hydration. Glycerin is a humectant that draws moisture from the air into the skin. Aloe vera adds additional hydration and soothes. Together, they create a powerful hydration boost.',
    tags: ['Hydrating', 'Plumping', 'Moisturizing'],
  },
  {
    id: '27',
    title: 'Licorice Root + Niacinamide Brightening',
    emoji: '🌿',
    description: 'Advanced brightening mask to fade hyperpigmentation and even skin tone.',
    benefits: ['Fades dark spots', 'Evens tone', 'Brightens complexion'],
    ingredients: [
      '1 tbsp licorice root powder',
      '1 tsp niacinamide powder (or crushed B3 supplement)',
      '1 tbsp plain yogurt',
      '1 tsp honey',
      '1/2 tsp lemon juice'
    ],
    instructions: [
      'Mix licorice root powder with niacinamide',
      'Add yogurt and honey',
      'Stir in lemon juice',
      'Apply to clean face',
      'Leave on for 15-20 minutes',
      'Rinse with cool water',
      'Use sunscreen after (increases sun sensitivity)'
    ],
    duration: '15-20 min',
    science: 'Licorice root contains glabridin, which inhibits tyrosinase (the enzyme that produces melanin), effectively fading dark spots. Niacinamide (vitamin B3) also inhibits melanosome transfer and improves skin barrier function. Together, they provide powerful brightening effects.',
    tags: ['Brightening', 'Dark spots', 'Evening tone'],
  },
  {
    id: '28',
    title: 'Dead Sea Mud + Epsom Salt Detox',
    emoji: '🧂',
    description: 'Mineral-rich detoxifying mask to draw out toxins and reduce inflammation.',
    benefits: ['Detoxifies', 'Reduces inflammation', 'Mineral boost'],
    ingredients: [
      '2 tbsp Dead Sea mud (or bentonite clay)',
      '1 tbsp Epsom salt (dissolved)',
      '1 tsp honey',
      '2-3 tbsp water'
    ],
    instructions: [
      'Dissolve Epsom salt in warm water',
      'Mix with Dead Sea mud',
      'Add honey',
      'Apply to clean face and neck',
      'Leave on for 10-15 minutes',
      'Rinse with warm water',
      'Moisturize after'
    ],
    duration: '10-15 min',
    science: 'Dead Sea mud is rich in magnesium, calcium, and potassium that reduce inflammation and improve skin barrier function. Epsom salt (magnesium sulfate) further reduces inflammation and helps draw out toxins. The combination provides deep detoxification.',
    tags: ['Detoxifying', 'Mineral-rich', 'Anti-inflammatory'],
  },
];

const DIYMasksPage: React.FC = () => {
  const { userProfile } = useAWSAuth();
  const { checkFeature, tier } = useSubscription();
  const [expandedMasks, setExpandedMasks] = useState<Set<string>>(new Set());
  const [savedMasks, setSavedMasks] = useState<Array<DIYMask & { scanId: string; scanCustomName?: string; savedAt: string }>>([]);

  useEffect(() => {
    // Load saved masks from localStorage (user-specific)
    if (!userProfile?.uid) return;
    const saved = JSON.parse(localStorage.getItem(`savedDIYMasks_${userProfile.uid}`) || '[]');
    // Limit masks based on tier: Plus = 10, Pro+ = unlimited
    let limitedMasks = saved;
    if (tier === 'plus') {
      limitedMasks = saved.slice(0, 10);
    }
    // For Pro+, show all saved masks
    setSavedMasks(limitedMasks);
  }, [userProfile?.uid, tier]);

  const toggleMask = (id: string) => {
    const newExpanded = new Set(expandedMasks);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedMasks(newExpanded);
  };

  return (
    <PageContainer>
      <Container>
        <Header>
          <Title>DIY Mask Library 🌿</Title>
          <Subtitle>
            Natural, science-backed recipes for glowing skin. All ingredients are acne-safe and gentle ✨
          </Subtitle>
        </Header>

        {/* Saved Masks from Scans */}
        {savedMasks.length > 0 && (
          <div style={{ marginBottom: '3rem', marginLeft: '-1.5rem', paddingLeft: '1.5rem' }}>
            <h2 style={{ fontSize: theme.typography.fontSize['2xl'], fontWeight: theme.typography.fontWeight.bold, marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Calendar size={24} />
              Personalized Masks from Your Scans
            </h2>
            <MasksGrid>
              {savedMasks.map((mask, index) => (
                <MaskCard key={`saved-${mask.id}-${mask.scanId}`} padding="lg" delay={`${index * 0.1}s`}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                    <Badge size="sm" variant="info">From Scan {mask.scanCustomName || mask.scanId}</Badge>
                    <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.tertiary }}>
                      {new Date(mask.savedAt).toLocaleDateString()}
                    </span>
                  </div>
                  <MaskImage $emoji={mask.emoji} />
                  <MaskTitle>{mask.title}</MaskTitle>
                  <MaskDescription>{mask.description}</MaskDescription>
                  
                  <TagsRow>
                    {mask.tags.map(tag => (
                      <Badge key={tag} size="sm">{tag}</Badge>
                    ))}
                  </TagsRow>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', fontSize: theme.typography.fontSize.sm, color: theme.colors.text.tertiary }}>
                    <Clock size={16} />
                    {mask.duration}
                  </div>

                  <ExpandButton onClick={() => toggleMask(`saved-${mask.id}`)}>
                    {expandedMasks.has(`saved-${mask.id}`) ? (
                      <>
                        Hide Recipe <ChevronUp />
                      </>
                    ) : (
                      <>
                        View Recipe <ChevronDown />
                      </>
                    )}
                  </ExpandButton>

                  <RecipeDetails $expanded={expandedMasks.has(`saved-${mask.id}`)}>
                    <RecipeSection>
                      <RecipeSectionTitle>
                        <Sparkles size={16} />
                        Benefits
                      </RecipeSectionTitle>
                      <RecipeList>
                        {mask.benefits.map((benefit, i) => (
                          <RecipeListItem key={`${mask.id || mask.scanId || index}-benefit-${i}`}>{benefit}</RecipeListItem>
                        ))}
                      </RecipeList>
                    </RecipeSection>

                    <RecipeSection>
                      <RecipeSectionTitle>
                        <Leaf size={16} />
                        Ingredients
                      </RecipeSectionTitle>
                      <RecipeList>
                        {mask.ingredients.map((ingredient, i) => (
                          <RecipeListItem key={`${mask.id || mask.scanId || index}-ingredient-${i}`}>{ingredient}</RecipeListItem>
                        ))}
                      </RecipeList>
                    </RecipeSection>

                    <RecipeSection>
                      <RecipeSectionTitle>Instructions</RecipeSectionTitle>
                      <RecipeList>
                        {mask.instructions.map((step, i) => (
                          <RecipeListItem key={`${mask.id || mask.scanId || index}-instruction-${i}`}>{step}</RecipeListItem>
                        ))}
                      </RecipeList>
                    </RecipeSection>

                    <ScienceNote>
                      <strong>💡 The Science:</strong> {mask.science}
                    </ScienceNote>
                  </RecipeDetails>
                </MaskCard>
              ))}
            </MasksGrid>
          </div>
        )}

        {/* Pro Mask Library - Only for Pro+ users (Pro and Elite) */}
        {(tier === 'pro' || tier === 'elite') ? (
          <div style={{ marginBottom: '3rem', marginLeft: '-1.5rem', paddingLeft: '1.5rem' }}>
            <h2 style={{ fontSize: theme.typography.fontSize['2xl'], fontWeight: theme.typography.fontWeight.bold, marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Sparkles size={24} />
              Pro Mask Library
            </h2>
            <MasksGrid>
              {masks.map((mask, index) => (
                <MaskCard key={mask.id} padding="lg" delay={`${index * 0.1}s`}>
                  <MaskImage $emoji={mask.emoji} />
                  <MaskTitle>{mask.title}</MaskTitle>
                  <MaskDescription>{mask.description}</MaskDescription>
                  
                  <TagsRow>
                    {mask.tags.map(tag => (
                      <Badge key={tag} size="sm">{tag}</Badge>
                    ))}
                  </TagsRow>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', fontSize: theme.typography.fontSize.sm, color: theme.colors.text.tertiary }}>
                    <Clock size={16} />
                    {mask.duration}
                  </div>

                  <ExpandButton onClick={() => toggleMask(mask.id)}>
                    {expandedMasks.has(mask.id) ? (
                      <>
                        Hide Recipe <ChevronUp />
                      </>
                    ) : (
                      <>
                        View Recipe <ChevronDown />
                      </>
                    )}
                  </ExpandButton>

                  <RecipeDetails $expanded={expandedMasks.has(mask.id)}>
                    <RecipeSection>
                      <RecipeSectionTitle>
                        <Sparkles size={16} />
                        Benefits
                      </RecipeSectionTitle>
                      <RecipeList>
                        {mask.benefits.map((benefit, i) => (
                          <RecipeListItem key={`pro-${mask.id}-benefit-${i}`}>{benefit}</RecipeListItem>
                        ))}
                      </RecipeList>
                    </RecipeSection>

                    <RecipeSection>
                      <RecipeSectionTitle>
                        <Leaf size={16} />
                        Ingredients
                      </RecipeSectionTitle>
                      <RecipeList>
                        {mask.ingredients.map((ingredient, i) => (
                          <RecipeListItem key={`pro-${mask.id}-ingredient-${i}`}>{ingredient}</RecipeListItem>
                        ))}
                      </RecipeList>
                    </RecipeSection>

                    <RecipeSection>
                      <RecipeSectionTitle>Instructions</RecipeSectionTitle>
                      <RecipeList>
                        {mask.instructions.map((step, i) => (
                          <RecipeListItem key={`pro-${mask.id}-instruction-${i}`}>{step}</RecipeListItem>
                        ))}
                      </RecipeList>
                    </RecipeSection>

                    <ScienceNote>
                      <strong>💡 The Science:</strong> {mask.science}
                    </ScienceNote>
                  </RecipeDetails>
                </MaskCard>
              ))}
            </MasksGrid>
          </div>
        ) : (
          <div style={{ marginBottom: '3rem' }}>
            <FeatureLock
              feature="Pro Mask Library"
              requiredTier="pro"
              message="Access to our full library of 28+ professional mask recipes is available in GlowLens Pro. Upgrade to unlock unlimited mask recipes beyond your personalized scans."
            />
          </div>
        )}
      </Container>
    </PageContainer>
  );
};

export default DIYMasksPage;
